/**
 * Settings — pairing, and the honest account of what is running where.
 *
 * This is also the unpaired screen: with no key nothing else can load, so the shell shows this
 * regardless of which tab is selected.
 */
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Text } from '@/components/ui/text';
import { api, describeError } from '@/api';
import { useStore } from '@/store';
import * as React from 'react';
import { Platform, ScrollView, View } from 'react-native';

export function SettingsScreen() {
  const { paired, setPaired, load } = useStore();
  const [key, setKey] = React.useState('');
  const [base, setBase] = React.useState(api.base);
  const [note, setNote] = React.useState('');
  const [health, setHealth] = React.useState<string>('');

  const pair = async () => {
    if (!key.trim()) return;
    await api.pair(key.trim(), base.trim() || undefined);
    setPaired(api.paired);
    setKey('');
    setNote('Paired. Loading…');
    await load();
    setNote('Paired.');
  };

  const check = async () => {
    setHealth('Checking…');
    try {
      const h = await api.get<{ db: boolean; keyConfigured: boolean; gatewayConfigured: boolean }>(
        '/api/health',
      );
      setHealth(
        `Worker up · D1 ${h.db ? 'bound' : 'MISSING'} · pairing key ${h.keyConfigured ? 'set' : 'MISSING'} · gateway key ${h.gatewayConfigured ? 'set' : 'not set'}`,
      );
    } catch (e) {
      setHealth(describeError(e));
    }
  };

  return (
    <ScrollView contentContainerClassName="gap-3 pb-6">
      {!paired ? (
        <Alert icon="◈">
          <AlertTitle>This device is not paired</AlertTitle>
          <AlertDescription>
            Everything here is personal — the bet ledger especially — so nothing loads without the
            key. Open the pairing link on this device, or paste the key below.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <Text variant="heading">Pairing</Text>
        </CardHeader>
        <CardContent className="gap-3 py-3">
          <View className="gap-1">
            <Label nativeID="key">Pairing key</Label>
            <Input
              aria-labelledby="key"
              value={key}
              onChangeText={setKey}
              placeholder={paired ? '•••••••• (already paired)' : 'psb-…'}
              autoCapitalize="none"
              autoCorrect={false}
              secureTextEntry
            />
            <Text variant="caption" className="text-muted-foreground">
              Easier: open the link with `?key=…` on this device. The key is consumed and stripped
              from the address immediately, so it does not sit in history or in a screenshot.
            </Text>
          </View>

          {Platform.OS !== 'web' ? (
            <View className="gap-1">
              <Label nativeID="base">API origin</Label>
              <Input
                aria-labelledby="base"
                value={base}
                onChangeText={setBase}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <Text variant="caption" className="text-muted-foreground">
                A native build has no origin of its own. The web build is served by the Worker and
                ignores this.
              </Text>
            </View>
          ) : null}

          <View className="flex-row flex-wrap gap-2">
            <Button size="sm" onPress={() => void pair()}>
              <Text>{paired ? 'Re-pair' : 'Pair'}</Text>
            </Button>
            <Button variant="outline" size="sm" onPress={() => void check()}>
              <Text>Check the Worker</Text>
            </Button>
            {paired ? (
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  void api.unpair().then(() => setPaired(false));
                }}>
                <Text>Unpair</Text>
              </Button>
            ) : null}
          </View>

          {note ? (
            <Text variant="small" className="text-muted-foreground">
              {note}
            </Text>
          ) : null}
          {health ? (
            <Text variant="small" className="text-muted-foreground">
              {health}
            </Text>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <Text variant="heading">What runs where</Text>
        </CardHeader>
        <CardContent className="gap-2 py-3">
          <Text variant="small" className="text-muted-foreground">
            <Text variant="small" className="text-foreground font-medium">
              This app
            </Text>{' '}
            reads and writes only through the Worker. It holds no keys beyond the pairing key and it
            never calls Polymarket, ESPN or Claude directly.
          </Text>
          <Text variant="small" className="text-muted-foreground">
            <Text variant="small" className="text-foreground font-medium">
              The Worker
            </Text>{' '}
            owns D1 and the schedules: the match refresh, the results accumulation, and a driver that
            runs prediction jobs through the shared AI gateway.
          </Text>
          <Text variant="small" className="text-muted-foreground">
            <Text variant="small" className="text-foreground font-medium">
              The PC
            </Text>{' '}
            does two things nothing else can. The AI gateway's executor runs predictions on a Claude
            subscription — so a queued prediction waits while the PC is asleep, and the card says so
            rather than pretending. And `trader` holds the Ed25519 money key, which is why Place
            records an intent here and the placement happens there.
          </Text>
          <Text variant="caption" className="text-muted-foreground">
            Statistics — the backtest and the propensity flags — run on this device. A walk-forward
            pass is far past what a Worker may spend on CPU.
          </Text>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <Text variant="heading">Reading a card</Text>
        </CardHeader>
        <CardContent className="gap-2 py-3">
          <Text variant="small" className="text-muted-foreground">
            A <Text variant="small" className="text-foreground font-semibold">bold</Text> bet name
            means value: the model recommended it, or its estimate beats the market by 5 points or
            more — and the league's own history does not contradict that.
          </Text>
          <Text variant="small" className="text-muted-foreground">
            An <Text variant="small" className="text-warning font-semibold">amber</Text> win % is
            that edge. It is the only signal that makes money: a bet that is likely but fairly priced
            returns nothing over time, and less than nothing after fees.
          </Text>
          <Text variant="small" className="text-muted-foreground">
            A high win % on its own is deliberately not highlighted at all.
          </Text>
          <Text variant="small" className="text-muted-foreground">
            <Text variant="small" className="text-primary">▲</Text> is the history-only propensity
            flag — a Poisson model over stored results, independent of both the model and the market.
          </Text>
        </CardContent>
      </Card>
    </ScrollView>
  );
}
